import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import React from 'react';
import { RootState } from '../../app/store';
import {
  addSeasonRate,
  deleteSeasonRate,
  deleteSeasonRatesByAccommodationId,
  fetchSeasonRateDetails,
  fetchSeasonRatesByAccommodationId,
  updateSeasonRate,
} from '../../common/api/seasonRatesService';
import { LOADING_STATUS } from '../../common/constants';

export interface RatePriceItem {
  duration: number;
  dailyPrice: number;
  discountedPrice?: number;
  dailyPurchasePrice: number;
}

export interface WeekdayPriceChangeItem {
  weekday: number;
  priceChange?: string;
}

export interface SeasonRateDetails {
  id?: string;
  seasonRateType?: string;
  isDeleted?: boolean;
  accommodationId: string;
  seasonName: string;
  startDate: string;
  startDateString?: string;
  endDate: string;
  endDateString?: string;
  priceForAdditionalDay: number;
  discountedPriceForAdditionalDay?: number;
  dailyPurchasePrice: number;
  commissionPercent: number;
  checkInTime: string;
  checkOutTime: string;
  seasonRateColorId?: string;
  seasonRateColorHex?: string;
  preparationDays?: number;
  minBookingDuration?: number;
  releaseInterval?: number;
  lastMinuteDiscount?: string;
  lastMinuteDaysUntilStart?: number;
  restrictedCheckInWeekdays: number[];
  restrictedCheckOutWeekdays: number[];
  mandatoryAddonNames?: string[];
  weekdayPriceChangeRules?: WeekdayPriceChangeItem[];
  ratePrices: RatePriceItem[];
}

export interface SeasonRatesState {
  seasonRateDetails: SeasonRateDetails;
  seasonRateItems: SeasonRateDetails[];
  seasonRateItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
}

const initialState: SeasonRatesState = {
  seasonRateDetails: {
    id: '',
    accommodationId: '',
    seasonName: '',
    startDate: '',
    endDate: '',
    isDeleted: false,
    priceForAdditionalDay: 0,
    dailyPurchasePrice: 0,
    commissionPercent: 0,
    checkInTime: '',
    checkOutTime: '',
    restrictedCheckInWeekdays: [],
    restrictedCheckOutWeekdays: [],
    weekdayPriceChangeRules: [],
    ratePrices: [],
  },
  seasonRateItems: [],
  seasonRateItemsStatus: LOADING_STATUS.IDLE,
};

export const getSeasonRatesAsync = createAsyncThunk(
  'seasonRates/getSeasonRates',
  async (
    {
      accommodationId,
      startFetchingRatesFrom,
    }: { accommodationId: string; startFetchingRatesFrom?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchSeasonRatesByAccommodationId(
        accommodationId,
        startFetchingRatesFrom,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getSeasonRateDetailsAsync = createAsyncThunk(
  'seasonRates/getSeasonRateDetails',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response = await fetchSeasonRateDetails(seasonRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveSeasonRateAsync = createAsyncThunk(
  'seasonRates/addSeasonRate',
  async (seasonRateData: SeasonRateDetails, { rejectWithValue }) => {
    try {
      const response = await addSeasonRate(seasonRateData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateSeasonRateAsync = createAsyncThunk(
  'seasonRates/updateSeasonRate',
  async (seasonRateData: SeasonRateDetails, { rejectWithValue }) => {
    try {
      const response = await updateSeasonRate(seasonRateData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteSeasonRateAsync = createAsyncThunk(
  'seasonRates/deleteSeasonRate',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response = await deleteSeasonRate(seasonRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteSeasonRatesByAccommodationIdAsync = createAsyncThunk(
  'seasonRates/deleteSeasonRatesByAccommodationId',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response =
        await deleteSeasonRatesByAccommodationId(accommodationId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const seasonRatesSlice = createSlice({
  name: 'seasonRates',
  initialState,
  reducers: {
    addSeasonRateToState: (state, action) => {
      state.seasonRateItems.push(action.payload);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getSeasonRatesAsync.pending, state => {
        state.seasonRateItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getSeasonRatesAsync.fulfilled, (state, action) => {
        state.seasonRateItemsStatus = LOADING_STATUS.IDLE;
        state.seasonRateItems = action.payload;
      })
      .addCase(getSeasonRatesAsync.rejected, state => {
        state.seasonRateItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getSeasonRateDetailsAsync.fulfilled, (state, action) => {
        state.seasonRateDetails = action.payload;
      })
      .addCase(deleteSeasonRateAsync.fulfilled, (state, action) => {
        const deletedSeasonRateId = action.payload;
        state.seasonRateItems = state.seasonRateItems.filter(
          item => item.id !== deletedSeasonRateId,
        );
      });
  },
});

export const { addSeasonRateToState } = seasonRatesSlice.actions;

export const selectSeasonRateDetails = (state: RootState) =>
  state.seasonRates.seasonRateDetails;
export const selectSeasonRateItems = (state: RootState) =>
  state.seasonRates.seasonRateItems;
export const selectSeasonRateItemsLoadingStatus = (state: RootState) =>
  state.seasonRates.seasonRateItemsStatus;

export default seasonRatesSlice.reducer;
